import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, LogOut, Plus, X, Trash2, Pencil, ImageIcon } from "lucide-react";
import { API, authHeader } from "@/config";
import { toast } from "sonner";

const TYPE_TABS = [
  ["all", "Alle"], ["quote", "Offertes"], ["appointment", "Afspraken"], ["rental", "Verhuur"],
  ["tow", "Sleepdienst"], ["mobile_diagnosis", "Mobiele diagnose"], ["keuring", "Autokeuring"], ["sale", "Te koop"],
];

const STATUSES = [
  ["nieuw", "Nieuw", "#3B82F6"], ["contact", "Contact opgenomen", "#8B5CF6"], ["afspraak", "Afspraak gemaakt", "#F59E0B"],
  ["behandeling", "In behandeling", "#06B6D4"], ["afgewerkt", "Afgewerkt", "#10B981"], ["geannuleerd", "Geannuleerd", "#EF4444"],
];

const PAYABLE_TYPES = ["appointment", "rental", "mobile_diagnosis", "tow", "keuring"];

const LABELS = {
  name: "Naam", phone: "Telefoon", email: "E-mail", address: "Adres", brand: "Merk", model: "Model", year: "Bouwjaar",
  plate: "Nummerplaat", vin: "VIN", km: "Km-stand", fuel: "Brandstof", transmission: "Transmissie", date: "Datum",
  time: "Tijd", slot: "Tijdsblok", service: "Dienst", problem: "Probleem", remarks: "Opmerkingen", preferred: "Contactvoorkeur",
  location: "Locatie", destination: "Bestemming", vehicle: "Voertuig", reason: "Reden", drives: "Rijdt", start: "Start", end: "Einde",
  delivery: "Ophalen/Levering", deliveryAddress: "Leveringsadres",
};

const apiFetch = async (path, options = {}) => {
  const res = await fetch(`${API}${path}`, { ...options, headers: { ...authHeader(), ...(options.headers || {}) } });
  if (res.status === 401) {
    localStorage.removeItem("mw_token");
    window.location.href = "/admin";
    throw new Error("unauthorized");
  }
  return res;
};

function RequestCard({ r, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState("");
  const status = STATUSES.find(([v]) => v === r.status) || STATUSES[0];

  const changeStatus = async (s) => {
    const res = await apiFetch(`/requests/${r.id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: s }),
    });
    if (res.ok) onUpdate();
  };

  const addNote = async () => {
    if (!note.trim()) return;
    const res = await apiFetch(`/requests/${r.id}/notes`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: note }),
    });
    if (res.ok) { setNote(""); onUpdate(); }
  };

  const requestPayment = async () => {
    const res = await apiFetch(`/requests/${r.id}/payment-request`, { method: "POST" });
    if (res.ok) { onUpdate(); toast.success("Betaalverzoek aangemaakt"); }
    else toast.error("Mislukt");
  };

  const payUrl = `${window.location.origin}/betalen/${r.id}`;

  return (
    <div className="card-dark p-5" data-testid={`request-card-${r.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="text-xs font-mono2 uppercase tracking-widest text-zinc-500">{r.type}</span>
          <h4 className="font-semibold text-white">{r.data?.name || "—"} <span className="text-zinc-500 font-normal text-sm">• {r.data?.phone}</span></h4>
          <span className="text-xs text-zinc-500">{new Date(r.created_at).toLocaleString("nl-BE")}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest rounded-full px-3 py-1 text-white" style={{ background: status[2] }} data-testid={`request-status-${r.id}`}>
            {status[1]}
          </span>
          {r.deposit?.status === "betaald" && (
            <span className="text-[10px] font-bold uppercase tracking-widest rounded-full px-3 py-1 bg-green-600 text-white" data-testid={`deposit-badge-${r.id}`}>€15 betaald</span>
          )}
          <button onClick={() => setExpanded(!expanded)} className="btn-outline !px-3 !py-1.5 text-xs" data-testid={`request-expand-${r.id}`}>
            {expanded ? "Sluiten" : "Details"}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-5 border-t border-zinc-800 pt-4 space-y-4">
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            {Object.entries(r.data || {}).filter(([k, v]) => v && k !== "gdpr" && typeof v !== "boolean").map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 border-b border-zinc-800/50 py-1.5">
                <span className="text-zinc-500">{LABELS[k] || k}</span>
                <span className="text-zinc-200 text-right break-all">{String(v)}</span>
              </div>
            ))}
          </div>
          {r.files?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {r.files.map((f, i) => (
                <a key={i} href={`${API.replace("/api", "")}${f}`} target="_blank" rel="noreferrer" data-testid={`request-file-${r.id}-${i}`}>
                  {/\.(jpg|jpeg|png|webp)$/.test(f) ? (
                    <img src={`${API.replace("/api", "")}${f}`} alt="" className="w-16 h-16 object-cover rounded-lg border border-zinc-700" />
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-zinc-400 border border-zinc-700 rounded-lg px-3 py-2"><ImageIcon size={13} />bestand</span>
                  )}
                </a>
              ))}
            </div>
          )}
          {PAYABLE_TYPES.includes(r.type) && (
            <div data-testid={`deposit-block-${r.id}`}>
              <label className="label-dark">Aanbetaling €15</label>
              {r.deposit?.status === "betaald" ? (
                <span className="text-xs font-bold text-green-400" data-testid={`deposit-paid-${r.id}`}>✓ Betaald{r.deposit.method === "stripe" ? " (online via Stripe)" : ""}</span>
              ) : r.deposit?.requested ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-xs bg-white/5 rounded px-2 py-1.5 text-zinc-300 break-all">{payUrl}</code>
                  <button onClick={() => { navigator.clipboard.writeText(payUrl); toast.success("Link gekopieerd"); }} className="btn-outline !px-3 !py-1.5 text-xs" data-testid={`paylink-copy-${r.id}`}>Kopieer link</button>
                  <a href={`https://wa.me/?text=${encodeURIComponent(`Hallo! Uw aanvraag bij Milano Worx is bevestigd. Betaal de aanbetaling van €15 via: ${payUrl}`)}`} target="_blank" rel="noreferrer" className="btn-wa !px-3 !py-1.5 text-xs" data-testid={`paylink-wa-${r.id}`}>Deel via WhatsApp</a>
                </div>
              ) : (
                <button onClick={requestPayment} className="btn-red !px-3 !py-1.5 text-xs" data-testid={`payreq-${r.id}`}>Betaalverzoek aanmaken</button>
              )}
            </div>
          )}
          <div>
            <label className="label-dark">Status</label>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map(([v, label, color]) => (
                <button key={v} onClick={() => changeStatus(v)} data-testid={`status-${r.id}-${v}`}
                  className={`text-[11px] font-bold uppercase tracking-wide rounded-full px-3 py-1.5 border transition-colors ${r.status === v ? "text-white" : "text-zinc-500 border-zinc-700 hover:text-white"}`}
                  style={r.status === v ? { background: color, borderColor: color } : {}}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label-dark">Interne notities</label>
            <div className="space-y-2">
              {(r.notes || []).map((n, i) => (
                <p key={i} className="text-xs text-zinc-400 bg-white/5 rounded-lg px-3 py-2">
                  {n.text} <span className="text-zinc-600">— {new Date(n.at).toLocaleDateString("nl-BE")}</span>
                </p>
              ))}
              <div className="flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notitie toevoegen..." className="input-dark !py-2 text-xs" data-testid={`note-input-${r.id}`} />
                <button onClick={addNote} className="btn-red !px-3 !py-2 text-xs shrink-0" data-testid={`note-add-${r.id}`}><Plus size={14} /></button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY_VEHICLE = { brand: "", model: "", year: 2023, fuel: "Benzine", transmission: "Manueel", seats: 5, price_day: 0, price_week: null, price_month: null, deposit: 0, available: true, category: "car", image_url: "", features: "" };

function VehicleForm({ initial, onClose, onSaved }) {
  const [v, setV] = useState(initial || EMPTY_VEHICLE);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    const res = await apiFetch("/upload", { method: "POST", body: fd });
    if (res.ok) {
      const { url } = await res.json();
      setV((prev) => ({ ...prev, image_url: url }));
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = { ...v, year: Number(v.year), seats: Number(v.seats), price_day: Number(v.price_day), deposit: Number(v.deposit), price_week: v.price_week ? Number(v.price_week) : null, price_month: v.price_month ? Number(v.price_month) : null, image_url: v.image_url || null };
    const isEdit = !!initial?.id;
    const res = await apiFetch(isEdit ? `/vehicles/${initial.id}` : "/vehicles", {
      method: isEdit ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    if (res.ok) { onSaved(); onClose(); toast.success("Opgeslagen"); }
    else toast.error("Opslaan mislukt");
  };

  const numField = (label, key) => (
    <div><label className="label-dark">{label}</label><input type="number" value={v[key] ?? ""} onChange={set(key)} className="input-dark" data-testid={`vf-${key}`} /></div>
  );

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose} data-testid="vehicle-form-modal">
      <div className="card-dark w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto rounded-b-none sm:rounded-b-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-heading font-bold text-lg text-white">{initial?.id ? "Voertuig bewerken" : "Voertuig toevoegen"}</h3>
          <button onClick={onClose} className="p-1.5 text-zinc-400 hover:text-white" data-testid="vehicle-form-close"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="grid sm:grid-cols-3 gap-4" data-testid="vehicle-form">
          <div><label className="label-dark">Merk</label><input required value={v.brand} onChange={set("brand")} className="input-dark" data-testid="vf-brand" /></div>
          <div><label className="label-dark">Model</label><input required value={v.model} onChange={set("model")} className="input-dark" data-testid="vf-model" /></div>
          {numField("Bouwjaar", "year")}
          <div><label className="label-dark">Brandstof</label>
            <select value={v.fuel} onChange={set("fuel")} className="input-dark" data-testid="vf-fuel">
              {["Benzine", "Diesel", "Hybride", "Elektrisch", "LPG"].map((f) => <option key={f} className="bg-[#121215]">{f}</option>)}
            </select>
          </div>
          <div><label className="label-dark">Transmissie</label>
            <select value={v.transmission} onChange={set("transmission")} className="input-dark" data-testid="vf-transmission">
              {["Manueel", "Automaat"].map((f) => <option key={f} className="bg-[#121215]">{f}</option>)}
            </select>
          </div>
          {numField("Zitplaatsen", "seats")}
          {numField("Prijs/dag €", "price_day")}
          {numField("Prijs/week €", "price_week")}
          {numField("Prijs/maand €", "price_month")}
          {numField("Borg €", "deposit")}
          <div><label className="label-dark">Categorie</label>
            <select value={v.category} onChange={set("category")} className="input-dark" data-testid="vf-category">
              <option value="car" className="bg-[#121215]">Auto</option>
              <option value="van" className="bg-[#121215]">Bestelwagen</option>
            </select>
          </div>
          <div className="sm:col-span-3">
            <label className="label-dark">Foto</label>
            <div className="flex items-center gap-3">
              {v.image_url && <img src={v.image_url.startsWith("/") ? `${API.replace("/api", "")}${v.image_url}` : v.image_url} alt="" className="w-20 h-14 object-cover rounded-lg border border-zinc-700" />}
              <label className="btn-outline !px-4 !py-2 text-xs cursor-pointer" data-testid="vf-photo-upload">
                {uploading ? "Uploaden..." : "Foto uploaden"}
                <input type="file" accept="image/*" className="hidden" onChange={upload} />
              </label>
            </div>
            <input value={v.image_url || ""} onChange={set("image_url")} placeholder="of afbeelding-URL" className="input-dark mt-2 !text-xs" data-testid="vf-image-url" />
          </div>
          <div className="sm:col-span-3"><label className="label-dark">Kenmerken</label><input value={v.features || ""} onChange={set("features")} className="input-dark" data-testid="vf-features" /></div>
          <label className="flex items-center gap-2 sm:col-span-3 text-sm text-zinc-300" data-testid="vf-available-label">
            <input type="checkbox" checked={v.available} onChange={set("available")} className="w-4 h-4 accent-[#E11D48]" data-testid="vf-available" /> Beschikbaar
          </label>
          <button type="submit" className="btn-red sm:col-span-3" data-testid="vf-submit">Opslaan</button>
        </form>
      </div>
    </div>
  );
}

const EMPTY_SALE = { brand: "", model: "", year: 2020, fuel: "Benzine", transmission: "Manueel", km: 0, price: 0, description: "", image_url: "", available: true };

function SaleVehicleForm({ initial, onClose, onSaved }) {
  const [v, setV] = useState(initial || EMPTY_SALE);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    const res = await apiFetch("/upload", { method: "POST", body: fd });
    if (res.ok) {
      const { url } = await res.json();
      setV((prev) => ({ ...prev, image_url: url }));
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = { ...v, year: Number(v.year), km: Number(v.km), price: Number(v.price), image_url: v.image_url || null, description: v.description || null };
    const isEdit = !!initial?.id;
    const res = await apiFetch(isEdit ? `/sale-vehicles/${initial.id}` : "/sale-vehicles", {
      method: isEdit ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    if (res.ok) { onSaved(); onClose(); toast.success("Opgeslagen"); }
    else toast.error("Opslaan mislukt");
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose} data-testid="sale-form-modal">
      <div className="card-dark w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto rounded-b-none sm:rounded-b-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-heading font-bold text-lg text-white">{initial?.id ? "Auto bewerken" : "Auto toevoegen"}</h3>
          <button onClick={onClose} className="p-1.5 text-zinc-400 hover:text-white" data-testid="sale-form-close"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="grid sm:grid-cols-3 gap-4" data-testid="sale-vehicle-form">
          <div><label className="label-dark">Merk</label><input required value={v.brand} onChange={set("brand")} className="input-dark" data-testid="sf-brand" /></div>
          <div><label className="label-dark">Model</label><input required value={v.model} onChange={set("model")} className="input-dark" data-testid="sf-model" /></div>
          <div><label className="label-dark">Bouwjaar</label><input type="number" value={v.year} onChange={set("year")} className="input-dark" data-testid="sf-year" /></div>
          <div><label className="label-dark">Brandstof</label>
            <select value={v.fuel} onChange={set("fuel")} className="input-dark" data-testid="sf-fuel">
              {["Benzine", "Diesel", "Hybride", "Elektrisch", "LPG"].map((f) => <option key={f} className="bg-[#121215]">{f}</option>)}
            </select>
          </div>
          <div><label className="label-dark">Transmissie</label>
            <select value={v.transmission} onChange={set("transmission")} className="input-dark" data-testid="sf-transmission">
              {["Manueel", "Automaat"].map((f) => <option key={f} className="bg-[#121215]">{f}</option>)}
            </select>
          </div>
          <div><label className="label-dark">Km-stand</label><input type="number" value={v.km} onChange={set("km")} className="input-dark" data-testid="sf-km" /></div>
          <div><label className="label-dark">Prijs €</label><input type="number" step="0.01" value={v.price} onChange={set("price")} className="input-dark" data-testid="sf-price" /></div>
          <div className="sm:col-span-2">
            <label className="label-dark">Foto</label>
            <div className="flex items-center gap-3">
              {v.image_url && <img src={v.image_url.startsWith("/") ? `${API.replace("/api", "")}${v.image_url}` : v.image_url} alt="" className="w-20 h-14 object-cover rounded-lg border border-zinc-700" />}
              <label className="btn-outline !px-4 !py-2 text-xs cursor-pointer" data-testid="sf-photo-upload">
                {uploading ? "Uploaden..." : "Foto uploaden"}
                <input type="file" accept="image/*" className="hidden" onChange={upload} />
              </label>
            </div>
          </div>
          <div className="sm:col-span-3"><label className="label-dark">Beschrijving</label><input value={v.description || ""} onChange={set("description")} className="input-dark" data-testid="sf-description" /></div>
          <label className="flex items-center gap-2 sm:col-span-3 text-sm text-zinc-300">
            <input type="checkbox" checked={v.available} onChange={set("available")} className="w-4 h-4 accent-[#E11D48]" data-testid="sf-available" /> Te koop (uitvinken = verkocht)
          </label>
          <button type="submit" className="btn-red sm:col-span-3" data-testid="sf-submit">Opslaan</button>
        </form>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [requests, setRequests] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [tab, setTab] = useState("all");
  const [view, setView] = useState("requests");
  const [vTab, setVTab] = useState("rental");
  const [saleVehicles, setSaleVehicles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await apiFetch("/requests");
      setRequests(await res.json());
      const vres = await apiFetch("/vehicles");
      setVehicles(await vres.json());
      const sres = await apiFetch("/sale-vehicles");
      setSaleVehicles(await sres.json());
    } catch {}
  };

  useEffect(() => {
    if (!localStorage.getItem("mw_token")) { navigate("/admin"); return; }
    apiFetch("/auth/me").then((r) => { if (r.ok) load(); }).catch(() => {});
  }, []);

  const filtered = tab === "all" ? requests : requests.filter((r) => r.type === tab);

  const deleteVehicle = async (id) => {
    if (!window.confirm("Voertuig verwijderen?")) return;
    const res = await apiFetch(`/vehicles/${id}`, { method: "DELETE" });
    if (res.ok) { load(); toast.success("Verwijderd"); }
  };

  const deleteSale = async (id) => {
    if (!window.confirm("Auto verwijderen?")) return;
    const res = await apiFetch(`/sale-vehicles/${id}`, { method: "DELETE" });
    if (res.ok) { load(); toast.success("Verwijderd"); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-[#0A0A0C]/90 backdrop-blur-md border-b border-zinc-800">
        <div className="container-x flex items-center justify-between py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#E11D48] flex items-center justify-center"><Wrench size={15} className="text-white" /></div>
            <span className="font-heading font-extrabold text-white">MILANO <span className="text-[#E11D48]">WORX</span> <span className="text-zinc-500 text-xs font-normal">Admin</span></span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setView("requests")} data-testid="admin-view-requests"
              className={`px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-lg ${view === "requests" ? "bg-[#E11D48] text-white" : "text-zinc-400"}`}>Aanvragen</button>
            <button onClick={() => setView("vehicles")} data-testid="admin-view-vehicles"
              className={`px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-lg ${view === "vehicles" ? "bg-[#E11D48] text-white" : "text-zinc-400"}`}>Voertuigen</button>
            <button onClick={() => { localStorage.removeItem("mw_token"); navigate("/admin"); }} data-testid="admin-logout-btn"
              className="p-2 text-zinc-400 hover:text-white"><LogOut size={17} /></button>
          </div>
        </div>
      </header>

      <div className="container-x py-8">
        {view === "requests" ? (
          <div data-testid="admin-pipeline-board">
            <div className="flex gap-2 overflow-x-auto pb-3">
              {TYPE_TABS.map(([v, label]) => (
                <button key={v} onClick={() => setTab(v)} data-testid={`admin-tab-${v}`}
                  className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider whitespace-nowrap border transition-colors ${tab === v ? "bg-[#E11D48] border-[#E11D48] text-white" : "border-zinc-700 text-zinc-400"}`}>
                  {label} ({v === "all" ? requests.length : requests.filter((r) => r.type === v).length})
                </button>
              ))}
            </div>
            <div className="mt-5 space-y-4">
              {filtered.map((r) => <RequestCard key={r.id} r={r} onUpdate={load} />)}
              {filtered.length === 0 && <p className="text-zinc-600 text-center py-12 text-sm">Geen aanvragen</p>}
            </div>
          </div>
        ) : (
          <div>
            <div className="flex gap-2 mb-4" data-testid="admin-vehicle-subtabs">
              {[["rental", "Verhuur"], ["sale", "Te koop"]].map(([v, label]) => (
                <button key={v} onClick={() => setVTab(v)} data-testid={`admin-vtab-${v}`}
                  className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border transition-colors ${vTab === v ? "bg-[#E11D48] border-[#E11D48] text-white" : "border-zinc-700 text-zinc-400"}`}>
                  {label} ({v === "rental" ? vehicles.length : saleVehicles.length})
                </button>
              ))}
            </div>
            <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-red !py-2.5 text-sm" data-testid="vehicle-add-btn">
              <Plus size={16} /> {vTab === "rental" ? "Voertuig toevoegen" : "Auto toevoegen"}
            </button>
            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(vTab === "rental" ? vehicles : saleVehicles).map((v) => (
                <div key={v.id} className="card-dark p-4" data-testid={`admin-vehicle-${v.id}`}>
                  <div className="flex gap-3">
                    {v.image_url && <img src={v.image_url.startsWith("/") ? `${API.replace("/api", "")}${v.image_url}` : v.image_url} alt="" className="w-24 h-16 object-cover rounded-lg border border-zinc-700" />}
                    <div className="min-w-0">
                      <h4 className="font-semibold text-white text-sm truncate">{v.brand} {v.model}</h4>
                      <p className="text-xs text-zinc-500">
                        {vTab === "rental"
                          ? `${v.category === "van" ? "Bestelwagen" : "Auto"} • €${v.price_day}/dag • Borg €${v.deposit}`
                          : `${v.year} • ${Number(v.km).toLocaleString("nl-BE")} km • €${Number(v.price).toLocaleString("nl-BE")}`}
                      </p>
                      <span className={`text-[10px] font-bold uppercase ${v.available ? "text-green-400" : "text-red-400"}`}>
                        {v.available ? (vTab === "rental" ? "Beschikbaar" : "Te koop") : (vTab === "rental" ? "Niet beschikbaar" : "Verkocht")}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => { setEditing(v); setShowForm(true); }} className="btn-outline flex-1 !py-1.5 text-xs" data-testid={`vehicle-edit-${v.id}`}><Pencil size={13} /> Bewerken</button>
                    <button onClick={() => (vTab === "rental" ? deleteVehicle(v.id) : deleteSale(v.id))} className="p-2 text-red-400 border border-zinc-700 rounded-lg hover:border-red-500" data-testid={`vehicle-delete-${v.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
              {(vTab === "rental" ? vehicles : saleVehicles).length === 0 && <p className="text-zinc-600 col-span-full py-10 text-center text-sm">Geen voertuigen</p>}
            </div>
          </div>
        )}
      </div>

      {showForm && (vTab === "rental"
        ? <VehicleForm initial={editing} onClose={() => setShowForm(false)} onSaved={load} />
        : <SaleVehicleForm initial={editing} onClose={() => setShowForm(false)} onSaved={load} />)}
    </div>
  );
}
